/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/guideline.js
 *
 * RECIP 1.0 E-4 — PSMA_STATUS enum simplified; getPsmaStatusFilters removed.
 *
 * E-4 changes:
 *   - PSMA_STATUS retains only CALCULATING (used for transient UI state during
 *     async SUV compute). All other values (POSITIVE, NEGATIVE, THRESHOLD,
 *     NA, LIVER_REF) are marked deprecated — they are never SET by E-state
 *     code but may still appear on stored ROIs. They are kept in the enum
 *     so old consumers don't crash on import.
 *   - getPsmaStatusFilters() removed (PSMA filter dropdown dropped in E-4 UI).
 *     FindingSection / AnalysisSection no longer render it.
 *   - matchPsmaStatusFilter() kept for backward compat but no longer called
 *     by any E-state UI component.
 *   - matchTrackingTypeFilter 'other' still includes LIVER_REFERENCE.
 */

export const BODY_PARTS = {
  BRAIN: 'brain',
  NECK: 'neck',
  BONE: 'bone',
  LUNG: 'lung',
  LIVER: 'liver',
  LYMPHNODE: 'lymph-node',
  ADRENALGLAND: 'adrenal-gland',
  PLEURA: 'pleura',
  PLEURALEFFUSION: 'pleural-effusion',
  SOFTTISSUE: 'soft-tissue',
  OTHERS: 'others',
};

export const TRACKING_TYPES = {
  TARGET: 'target',
  NONTARGET: 'non-target',
  NEW: 'new',
  NEW_TARGET: 'new-lesion-target',
  NEW_NONTARGET: 'new-lesion-non-target',
  PSMA_NEGATIVE: 'psma-negative',
  NEW_PSMA_POSITIVE: 'new-psma-positive',
  NEW_PSMA_NEGATIVE: 'new-psma-negative',
  BLOOD_POOL: 'blood-pool',
  LIVER_REFERENCE: 'liver-reference',
  LESION: 'lesion',
  NEW_LESION: 'new-lesion',
  OTHER: 'other',
  NOTINCLUDED: 'not-included',
  NONE: 'none',
  ISOLATED: 'isolated',
};

/**
 * PSMA per-timepoint status enum (E-4 simplified).
 *
 * CALCULATING is the only live value set by E-state code.
 * All other values are deprecated legacy — kept so any code that imports
 * PSMA_STATUS.POSITIVE etc. doesn't crash on undefined.
 */
export const PSMA_STATUS = {
  CALCULATING: 'calculating', // transient UI state during async compute
  // ── Deprecated (D-state) — never set by E-state code ──────────────────
  /** @deprecated Use psmaPosVolumeByTimepoint instead. */
  POSITIVE: 'positive',
  /** @deprecated Use psmaPosVolumeByTimepoint instead. */
  NEGATIVE: 'negative',
  /** @deprecated Use psmaPosVolumeByTimepoint instead. */
  THRESHOLD: 'threshold',
  /** @deprecated Use psmaPosVolumeByTimepoint instead. */
  NA: 'na',
  /** @deprecated Use psmaPosVolumeByTimepoint instead. */
  LIVER_REF: 'liver-ref',
};

export const RECIP_TUMOUR_TYPES = new Set([
  TRACKING_TYPES.LESION,
  TRACKING_TYPES.NEW_LESION,
]);

/** Lesion-type trackingTypes for non-RECIP guidelines (RECIST/mRECIST/iRECIST/RANO-BM).
 *  Used to restrict 'All Lesions' filter to true lesions, excluding NOTINCLUDED/NONE/aorta/liver-ref. */
const NON_RECIP_LESION_TYPES = new Set([
  TRACKING_TYPES.TARGET,
  TRACKING_TYPES.NONTARGET,
  TRACKING_TYPES.NEW,
  TRACKING_TYPES.NEW_TARGET,
  TRACKING_TYPES.NEW_NONTARGET,
  TRACKING_TYPES.LESION, // legacy migration compat
  TRACKING_TYPES.NEW_LESION, // legacy migration compat
]);

export const OVERALL_RESPONSE = {
  COMPLETE_RESPONSE: 'CR',
  PARTIAL_RESPONSE: 'PR',
  STABLE_DISEASE: 'SD',
  PSEUDO_PROGRESSIVE_DISEASE: 'Pseudo-PD',
  PROGRESSIVE_DISEASE: 'PD',
  NOT_EVALUABLE: 'NE',
  NONE: 'NED',
  iCR: 'iCR',
  iPR: 'iPR',
  iSD: 'iSD',
  iUPD: 'iUPD',
  iCPD: 'iCPD',
};

export const TARGET_RESPONSE = {
  COMPLETE_RESPONSE: 'CR',
  PARTIAL_RESPONSE: 'PR',
  STABLE_DISEASE: 'SD',
  PROGRESSIVE_DISEASE: 'PD',
  NOT_EVALUABLE: 'NE',
  NONE: 'NED',
  iCR: 'iCR',
  iPR: 'iPR',
  iSD: 'iSD',
  iUPD: 'iUPD',
  iCPD: 'iCPD',
};

export const NON_TARGET_RESPONSE = {
  COMPLETE_RESPONSE: 'CR',
  NON_PROGRESSIVE_RESPONSE: 'Non-CR/Non-PD',
  PROGRESSIVE_DISEASE: 'PD',
  NOT_EVALUABLE: 'NE',
  NONE: 'NED',
  iCR: 'iCR',
  iNN: 'Non-iCR/Non-iUPD',
  iUPD: 'iUPD',
  iCPD: 'iCPD',
};

export const NEW_LESION_RESPONSE = {
  PRESENT: 'Unequivocal',
  ABSENT: 'Absent',
  UNCONFIRMED: 'Equivocal',
  EQUIVOCAL: 'Equivocal',
  UNEQUIVOCAL: 'Unequivocal',
  NOT_EVALUABLE: 'NE',
  NONE: 'NED',
  iCR: 'iCR',
  iPR: 'iPR',
  iSD: 'iSD',
  iNN: 'Non-iCR/Non-iUPD',
  iUPD: 'iUPD',
  iCPD: 'iCPD',
};

export const MEASUREMENT_TECHNIQUE = {
  UNIDIMENSIONAL: 'UNIDIMENSIONAL',
  BIDIMENSIONAL: 'BIDIMENSIONAL',
  VOLUMETRIC: 'VOLUMETRIC',
};

export const STEROIDS_USE = {
  NONE: 'None',
  DECREASED: 'Decreased',
  STABLE: 'Stable',
  INCREASED: 'Increased',
  NOT_APPLICABLE: 'N/A',
};

export const CLINICAL_STATUS = {
  WORSE: 'Worse',
  STABLE: 'Stable',
  IMPROVED: 'Improved',
  NOT_APPLICABLE: 'N/A',
};

export const BRAIN_LOCATION = {
  L_Frn: 'Left Frontal',
  R_Frn: 'Right Frontal',
  L_Temp: 'Left Temporal',
  R_Temp: 'Right Temporal',
  L_Par: 'Left Parietal',
  R_Par: 'Right Parietal',
  L_Occ: 'Left Occipital',
  R_Occ: 'Right Occipital',
  L_Cere: 'Left Cerebellum',
  R_Cere: 'Right Cerebellum',
  BS: 'Brainstem',
  L_Caud: 'Left Caudate',
  R_Caud: 'Right Caudate',
  L_Puta: 'Left Putamen',
  R_Puta: 'Right Putamen',
  L_Thal: 'Left Thalamus',
  R_Thal: 'Right Thalamus',
  L_GP: 'Left Globus Pallidus',
  R_GP: 'Right Globus Pallidus',
};

export function getMeasurementTechniques() {
  return [
    ['BIDIMENSIONAL', 'Bidimensional'],
    ['VOLUMETRIC', 'Volumetric'],
  ];
}

export function getMeasurementLesionTypes(guideline) {
  switch (guideline) {
    case 'iRECIST':
      return [
        { id: 'target', label: 'Target' },
        { id: 'non-target', label: 'Non-target' },
        { id: 'new-lesion-target', label: 'NEW(Target)' },
        { id: 'new-lesion-non-target', label: 'NEW(Non-Target)' },
        { id: 'not-included', label: '---' },
      ];
    case 'RECIP10':
      return [
        { id: 'lesion', label: 'Lesion' },
        { id: 'new-lesion', label: 'New Lesion' },
        { id: 'liver-reference', label: 'Liver Reference' },
        { id: 'blood-pool', label: 'Blood Pool' },
        { id: 'other', label: 'Other' },
      ];
    default:
      return [
        { id: 'target', label: 'Target' },
        { id: 'non-target', label: 'Non-target' },
        { id: 'new', label: 'New' },
        { id: 'not-included', label: '---' },
      ];
  }
}

export function getLesionTypes(guideline) {
  switch (guideline) {
    case 'iRECIST':
      return [
        { id: 'target', label: 'T' },
        { id: 'non-target', label: 'NT' },
        { id: 'new-lesion-target', label: 'NEW(T)' },
        { id: 'new-lesion-non-target', label: 'NEW(NT)' },
      ];
    case 'RECIP10':
      return [
        { id: 'lesion', label: 'Lesion' },
        { id: 'new-lesion', label: 'NEW' },
        { id: 'liver-reference', label: 'Liver Ref' },
        { id: 'blood-pool', label: 'BP' },
        { id: 'other', label: 'Other' },
      ];
    default:
      return [
        { id: 'target', label: 'Target' },
        { id: 'non-target', label: 'Non-target' },
        { id: 'new', label: 'New' },
      ];
  }
}

export function getBodyParts() {
  return [
    ['all', 'Whole Body'],
    ['brain', 'Brain'],
    ['neck', 'Neck'],
    ['bone', 'Bone'],
    ['soft-tissue', 'Soft Tissue'],
    ['liver', 'Liver'],
    ['lung', 'Lung'],
    ['lymph-node', 'Lymph Node'],
    ['adrenal-gland', 'Adrenal Gland'],
    ['pleural-effusion', 'Pleural Effusion'],
  ];
}

export function getTrackingTypes(guideline) {
  switch (guideline) {
    case 'iRECIST':
      return [
        ['all', 'All Lesions'],
        ['measurable', 'All Measurables'],
        ['target', 'Target Lesions'],
        ['non-target', 'Non-target Lesions'],
        ['new-lesion-target', 'New Lesion Targets'],
        ['new-lesion-non-target', 'New Lesion Non-targets'],
        ['other', 'Others'],
        ['all-rois', 'All ROIs'],
        ['selected', 'Selected Lesion'],
      ];
    case 'RECIP10':
      return [
        ['all', 'All Lesions'],
        ['lesion', 'Lesions'],
        ['new-lesion', 'New Lesions'],
        ['blood-pool', 'Blood Pool'],
        ['other', 'Other / Liver Ref'],
        ['all-rois', 'All ROIs'],
        ['selected', 'Selected Lesion'],
      ];
    default:
      return [
        ['all', 'All Lesions'],
        ['measurable', 'All Measurables'],
        ['target', 'Target Lesions'],
        ['non-target', 'Non-target Lesions'],
        ['new', 'New Lesions'],
        ['other', 'Others'],
        ['all-rois', 'All ROIs'],
        ['selected', 'Selected Lesion'],
      ];
  }
}

export function matchTrackingTypeFilter(
  roi,
  filterValue,
  guidelineId,
  selectedROINumber
) {
  if (filterValue === 'selected') return roi.ROINumber === selectedROINumber;
  if (guidelineId === 'RECIP10') {
    const t = roi.trackingType;
    switch (filterValue) {
      case 'all':
        return t === TRACKING_TYPES.LESION || t === TRACKING_TYPES.NEW_LESION;
      case 'all-rois':
        return !!t;
      case 'lesion':
      case 'new-lesion':
      case 'blood-pool':
        return t === filterValue;
      case 'other':
        return (
          t === TRACKING_TYPES.OTHER || t === TRACKING_TYPES.LIVER_REFERENCE
        );
      default:
        return false;
    }
  }
  switch (filterValue) {
    case 'all':
      return NON_RECIP_LESION_TYPES.has(roi.trackingType);
    case 'all-rois':
      return !!roi.trackingType;
    case 'other':
      return (
        !!roi.trackingType && !NON_RECIP_LESION_TYPES.has(roi.trackingType)
      );
    case 'measurable':
      return (
        roi.measurable && ['target', 'non-target'].includes(roi.trackingType)
      );
    case 'new':
      return ['new', 'new-lesion-target', 'new-lesion-non-target'].includes(
        roi.trackingType
      );
    case 'target':
    case 'non-target':
    case 'new-lesion-target':
    case 'new-lesion-non-target':
      return roi.trackingType === filterValue;
    default:
      return false;
  }
}

/** @deprecated PSMA filter dropdown removed in E-4. Kept for compat. */
export function matchPsmaStatusFilter(roi, filterValue, ctSeriesUid) {
  if (!filterValue || filterValue === 'all') return true;
  return false;
}

export function normalizeTrackingTypeForGuideline(rawKey, guidelineId) {
  if (!rawKey) return undefined;
  if (guidelineId === 'RECIP10' && rawKey === 'NEW')
    return TRACKING_TYPES.NEW_LESION;
  return TRACKING_TYPES[rawKey];
}

export function getDefaultNewTrackingType(guidelineId) {
  if (guidelineId === 'RECIP10') return TRACKING_TYPES.LESION;
  return TRACKING_TYPES.NEW;
}

export function getResponseTypes(guideline) {
  switch (guideline) {
    case 'iRECIST':
      return {
        TARGET_RESPONSE: [
          { value: 'iCR', label: 'iCR' },
          { value: 'iPR', label: 'iPR' },
          { value: 'iSD', label: 'iSD' },
          { value: 'iUPD', label: 'iUPD' },
          { value: 'iCPD', label: 'iCPD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
        NON_TARGET_RESPONSE: [
          { value: 'iCR', label: 'iCR' },
          { value: 'Non-iCR/Non-iUPD', label: 'Non-iCR/Non-iUPD' },
          { value: 'iUPD', label: 'iUPD' },
          { value: 'iCPD', label: 'iCPD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
        NEW_LESION_TARGET_RESPONSE: [
          { value: 'iCR', label: 'iCR' },
          { value: 'iPR', label: 'iPR' },
          { value: 'iSD', label: 'iSD' },
          { value: 'iUPD', label: 'iUPD' },
          { value: 'iCPD', label: 'iCPD' },
          { value: 'Absent', label: 'Absent' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED', disabled: true },
        ],
        NEW_LESION_NON_TARGET_RESPONSE: [
          { value: 'iCR', label: 'iCR' },
          { value: 'Non-iCR/Non-iUPD', label: 'Non-iCR/Non-iUPD' },
          { value: 'iUPD', label: 'iUPD' },
          { value: 'iCPD', label: 'iCPD' },
          { value: 'Absent', label: 'Absent' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED', disabled: true },
        ],
        USE_OF_STEROIDS: [{ value: 'N/A', label: 'N/A' }],
        CLINICAL_STATUS: [
          { value: 'N/A', label: 'N/A' },
          { value: 'Worse', label: 'Worse' },
          { value: 'Stable', label: 'Stable' },
          { value: 'Improved', label: 'Improved' },
        ],
        OVERALL_RESPONSE: [
          { value: 'iCR', label: 'iCR' },
          { value: 'iPR', label: 'iPR' },
          { value: 'iSD', label: 'iSD' },
          { value: 'Non-iCR/Non-iUPD', label: 'Non-iCR/Non-iUPD' },
          { value: 'iUPD', label: 'iUPD' },
          { value: 'iCPD', label: 'iCPD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
      };
    case 'RECIP10': {
      const volumeResponse = [
        { value: 'CR', label: 'CR' },
        { value: 'PR', label: 'PR' },
        { value: 'SD', label: 'SD' },
        { value: 'PD', label: 'PD' },
        { value: 'NE', label: 'NE' },
        { value: 'NED', label: 'NED' },
      ];
      return {
        TARGET_RESPONSE: volumeResponse,
        NON_TARGET_RESPONSE: [],
        NEW_LESION_RESPONSE: [
          { value: 'Unequivocal', label: 'Yes (Present)' },
          { value: 'Absent', label: 'No (Absent)' },
          { value: 'NE', label: 'NE' },
        ],
        USE_OF_STEROIDS: [{ value: 'N/A', label: 'N/A' }],
        CLINICAL_STATUS: [{ value: 'N/A', label: 'N/A' }],
        OVERALL_RESPONSE: [
          { value: 'CR', label: 'RECIP-CR' },
          { value: 'PR', label: 'RECIP-PR' },
          { value: 'SD', label: 'RECIP-SD' },
          { value: 'PD', label: 'RECIP-PD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
      };
    }
    default:
      return {
        TARGET_RESPONSE: [
          { value: 'CR', label: 'CR' },
          { value: 'PR', label: 'PR' },
          { value: 'SD', label: 'SD' },
          { value: 'PD', label: 'PD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
        NON_TARGET_RESPONSE: [
          { value: 'CR', label: 'CR' },
          { value: 'Non-CR/Non-PD', label: 'Non-CR/Non-PD' },
          { value: 'PD', label: 'PD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
        NEW_LESION_RESPONSE: [
          { value: 'Unequivocal', label: 'Unequivocal' },
          { value: 'Equivocal', label: 'Equivocal' },
          { value: 'Absent', label: 'Absent' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED', disabled: true },
        ],
        USE_OF_STEROIDS: [
          { value: 'N/A', label: 'N/A' },
          { value: 'None', label: 'None' },
          { value: 'Decreased', label: 'Decreased' },
          { value: 'Stable', label: 'Stable' },
          { value: 'Increased', label: 'Increased' },
        ],
        CLINICAL_STATUS: [
          { value: 'N/A', label: 'N/A' },
          { value: 'Worse', label: 'Worse' },
          { value: 'Stable', label: 'Stable' },
          { value: 'Improved', label: 'Improved' },
        ],
        OVERALL_RESPONSE: [
          { value: 'CR', label: 'CR' },
          { value: 'PR', label: 'PR' },
          { value: 'SD', label: 'SD' },
          { value: 'Non-CR/Non-PD', label: 'Non-CR/Non-PD' },
          { value: 'PD', label: 'PD' },
          { value: 'NE', label: 'NE' },
          { value: 'NED', label: 'NED' },
        ],
      };
  }
}
